import { debugLogger } from '@/utils/debugLogger';

interface CacheEntry {
  data: any;
  timestamp: number;
  version: string;
  etag?: string;
  expiresAt?: number; // Custom expiration timestamp
}

interface CacheMetadata {
  totalFiles: number;
  lastModified: string;
  buckets: string[];
}

export class LocalStorageCacheService {
  private static instance: LocalStorageCacheService;
  private readonly CACHE_PREFIX = 'optimized_media_';
  private readonly METADATA_KEY = 'cache_metadata';
  private readonly CACHE_VERSION = '2.0';
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  static getInstance(): LocalStorageCacheService {
    if (!LocalStorageCacheService.instance) {
      LocalStorageCacheService.instance = new LocalStorageCacheService();
    }
    return LocalStorageCacheService.instance;
  }

  private getStorageKey(key: string): string {
    return `${this.CACHE_PREFIX}${key}`;
  }

  set(key: string, data: any, metadata?: any): boolean {
    try {
      // Use custom expiresIn (in seconds) if provided, otherwise use default
      const expiresInMs = metadata?.expiresIn 
        ? metadata.expiresIn * 1000 
        : this.CACHE_DURATION;
      
      const entry: CacheEntry = {
        data,
        timestamp: Date.now(),
        version: this.CACHE_VERSION,
        etag: metadata?.etag,
        expiresAt: Date.now() + expiresInMs
      };

      const serialized = JSON.stringify(entry);
      
      // Check storage quota
      const estimatedSize = new Blob([serialized]).size;
      if (estimatedSize > 5 * 1024 * 1024) { // 5MB limit
        debugLogger.warn('[LocalCache] Data too large, skipping cache');
        return false;
      }

      localStorage.setItem(this.getStorageKey(key), serialized);
      this.updateMetadata(key, data);
      return true;
    } catch (error) {
      debugLogger.error('[LocalCache] Error setting cache:', error);
      this.clearExpired(); // Try to free up space
      return false;
    }
  }

  get(key: string): any | null {
    try {
      const item = localStorage.getItem(this.getStorageKey(key));
      if (!item) return null;

      const entry: CacheEntry = JSON.parse(item);
      
      // Check version compatibility
      if (entry.version !== this.CACHE_VERSION) {
        this.remove(key);
        return null;
      }

      // Check expiration using expiresAt if available, otherwise fallback to CACHE_DURATION
      const expiresAt = entry.expiresAt || (entry.timestamp + this.CACHE_DURATION);
      if (Date.now() > expiresAt) {
        this.remove(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      debugLogger.error('[LocalCache] Error getting cache:', error);
      this.remove(key);
      return null;
    }
  }

  isValid(key: string, serverMetadata?: any): boolean {
    try {
      const item = localStorage.getItem(this.getStorageKey(key));
      if (!item) return false;

      const entry: CacheEntry = JSON.parse(item);
      
      // Check version compatibility
      if (entry.version !== this.CACHE_VERSION) {
        return false;
      }
      
      // Check expiration using expiresAt if available, otherwise fallback to CACHE_DURATION
      const expiresAt = entry.expiresAt || (entry.timestamp + this.CACHE_DURATION);
      if (Date.now() > expiresAt) {
        return false;
      }

      // Check against server metadata if provided
      if (serverMetadata?.etag && entry.etag !== serverMetadata.etag) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(this.getStorageKey(key));
      this.removeFromMetadata(key);
    } catch (error) {
      debugLogger.error('[LocalCache] Error removing cache:', error);
    }
  }

  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
      localStorage.removeItem(this.METADATA_KEY);
    } catch (error) {
      debugLogger.error('[LocalCache] Error clearing cache:', error);
    }
  }

  clearExpired(): void {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          try {
            const item = localStorage.getItem(key);
            if (item) {
              const entry: CacheEntry = JSON.parse(item);
              // Use expiresAt if available, otherwise fallback to CACHE_DURATION
              const expiresAt = entry.expiresAt || (entry.timestamp + this.CACHE_DURATION);
              if (now > expiresAt) {
                localStorage.removeItem(key);
              }
            }
          } catch {
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      debugLogger.error('[LocalCache] Error clearing expired cache:', error);
    }
  }

  private updateMetadata(key: string, data: any): void {
    try {
      const metadata = this.getMetadata();
      metadata.buckets = [...new Set([...metadata.buckets, key])];
      
      if (Array.isArray(data)) {
        metadata.totalFiles += data.length;
      }
      
      metadata.lastModified = new Date().toISOString();
      localStorage.setItem(this.METADATA_KEY, JSON.stringify(metadata));
    } catch (error) {
      debugLogger.error('[LocalCache] Error updating metadata:', error);
    }
  }

  private removeFromMetadata(key: string): void {
    try {
      const metadata = this.getMetadata();
      metadata.buckets = metadata.buckets.filter(b => b !== key);
      localStorage.setItem(this.METADATA_KEY, JSON.stringify(metadata));
    } catch (error) {
      debugLogger.error('[LocalCache] Error removing from metadata:', error);
    }
  }

  private getMetadata(): CacheMetadata {
    try {
      const item = localStorage.getItem(this.METADATA_KEY);
      if (item) {
        return JSON.parse(item);
      }
    } catch (error) {
      debugLogger.error('[LocalCache] Error getting metadata:', error);
    }
    
    return {
      totalFiles: 0,
      lastModified: new Date().toISOString(),
      buckets: []
    };
  }

  getStats() {
    const metadata = this.getMetadata();
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.CACHE_PREFIX));
    
    return {
      ...metadata,
      cacheKeys: keys.length,
      storageUsed: this.estimateStorageUsage()
    };
  }

  private estimateStorageUsage(): number {
    try {
      let total = 0;
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          const item = localStorage.getItem(key);
          if (item) {
            total += new Blob([item]).size;
          }
        }
      });
      return total;
    } catch {
      return 0;
    }
  }
}

export const localStorageCacheService = LocalStorageCacheService.getInstance();
